import {
  type AutoTopUpConfigResponseDto,
  BillingApi,
  BillingControllerListInvoicesDirectionEnum,
  type DeleteAutoTopUpConfigResponseDto,
  type EmbeddedCheckoutResponseDto,
  type ListInvoicesResponseDto,
  type RedeemCodeResponseDto,
  type TopUpResponseDto,
} from '@saaga/billingv2-sdk'
import { useCallback, useMemo } from 'react'

import { parseBillingError } from '@/utils/billing-error.util'

import { useBillingConfig } from './use-billing-config.hook'

export interface AutoTopUpConfig {
  threshold: number
  topUpAmount: number
  isEnabled: boolean
}

export interface UseBillingOperationsReturn {
  getBillingClient: () => Promise<BillingApi>
  topUp: (amountDollars: number) => Promise<TopUpResponseDto>
  topUpSession: (amountDollars: number) => Promise<EmbeddedCheckoutResponseDto>
  redeemCode: (code: string) => Promise<RedeemCodeResponseDto>
  listInvoices: (
    limit?: number,
    cursor?: string,
    direction?: 'forward' | 'backward'
  ) => Promise<ListInvoicesResponseDto>
  getAutoTopUpConfig: () => Promise<AutoTopUpConfigResponseDto | null>
  upsertAutoTopUpConfig: (config: AutoTopUpConfig) => Promise<AutoTopUpConfigResponseDto>
  deleteAutoTopUpConfig: () => Promise<DeleteAutoTopUpConfigResponseDto>
}

const MICROS_PER_DOLLAR = 1_000_000
const toMicros = (dollars: number): string => (dollars * MICROS_PER_DOLLAR).toString()

const getDirectionEnum = (
  direction: 'forward' | 'backward'
): BillingControllerListInvoicesDirectionEnum =>
  direction === 'forward'
    ? BillingControllerListInvoicesDirectionEnum.Forward
    : BillingControllerListInvoicesDirectionEnum.Backward

const executeWithErrorHandling = async <T>(fn: () => Promise<T>): Promise<T> => {
  try {
    return await fn()
  } catch (err) {
    throw new Error(await parseBillingError(err))
  }
}

const createTopUpOperation =
  (getClient: () => Promise<BillingApi>) =>
  (amountDollars: number): Promise<TopUpResponseDto> =>
    executeWithErrorHandling(() =>
      getClient().then((client) =>
        client.billingControllerTopUp({ topUpDto: { amount: toMicros(amountDollars) } })
      )
    )

const createTopUpSessionOperation =
  (getClient: () => Promise<BillingApi>) =>
  (amountDollars: number): Promise<EmbeddedCheckoutResponseDto> =>
    executeWithErrorHandling(() =>
      getClient().then((client) =>
        client.billingControllerTopUpSession({ topUpDto: { amount: toMicros(amountDollars) } })
      )
    )

const createRedeemCodeOperation =
  (getClient: () => Promise<BillingApi>) =>
  (code: string): Promise<RedeemCodeResponseDto> =>
    executeWithErrorHandling(() =>
      getClient().then((client) => client.billingControllerRedeemCode({ redeemCodeDto: { code } }))
    )

const createListInvoicesOperation =
  (getClient: () => Promise<BillingApi>) =>
  (limit = 25, cursor?: string, direction: 'forward' | 'backward' = 'forward') =>
    executeWithErrorHandling(() =>
      getClient().then((client) =>
        client.billingControllerListInvoices({
          limit,
          cursor,
          direction: getDirectionEnum(direction),
        })
      )
    )

const createGetAutoTopUpConfigOperation =
  (getClient: () => Promise<BillingApi>) =>
  async (): Promise<AutoTopUpConfigResponseDto | null> => {
    try {
      const client = await getClient()
      return await client.billingControllerGetAutoTopUpConfig()
    } catch {
      return null
    }
  }

const createUpsertAutoTopUpConfigOperation =
  (getClient: () => Promise<BillingApi>) =>
  (config: AutoTopUpConfig): Promise<AutoTopUpConfigResponseDto> =>
    executeWithErrorHandling(() =>
      getClient().then((client) =>
        client.billingControllerUpsertAutoTopUpConfig({
          updateAutoTopUpConfigDto: {
            thresholdAmount: toMicros(config.threshold),
            topUpAmount: toMicros(config.topUpAmount),
            enabled: config.isEnabled,
          },
        })
      )
    )

const createDeleteAutoTopUpConfigOperation =
  (getClient: () => Promise<BillingApi>) => (): Promise<DeleteAutoTopUpConfigResponseDto> =>
    executeWithErrorHandling(() =>
      getClient().then((client) => client.billingControllerDeleteAutoTopUpConfig())
    )

export const useBillingOperations = (): UseBillingOperationsReturn => {
  const { createConfig } = useBillingConfig()

  const getBillingClient = useCallback(
    async () => new BillingApi(await createConfig()),
    [createConfig]
  )

  return useMemo(
    () => ({
      getBillingClient,
      topUp: createTopUpOperation(getBillingClient),
      topUpSession: createTopUpSessionOperation(getBillingClient),
      redeemCode: createRedeemCodeOperation(getBillingClient),
      listInvoices: createListInvoicesOperation(getBillingClient),
      getAutoTopUpConfig: createGetAutoTopUpConfigOperation(getBillingClient),
      upsertAutoTopUpConfig: createUpsertAutoTopUpConfigOperation(getBillingClient),
      deleteAutoTopUpConfig: createDeleteAutoTopUpConfigOperation(getBillingClient),
    }),
    [getBillingClient]
  )
}
